import React, {useState} from 'react';
import {Box, Typography, Accordion, AccordionSummary, Button, TextField, MenuItem} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Close, HighlightOutlined} from '@mui/icons-material';

interface ParameterOptions {
    type: string;
    type_human: string;
    description: string;
    options: string[];
}

function isParameterOptions(param: Parameter): param is ParameterOptions {
    return (param as ParameterOptions).options !== undefined;
}

// This will fail if same key has different human readables depending on context.
const optionKeyToHumanMapping: Map<string, string> = new Map([
    ['rld', 'Regulated log transformation (rld, rlog)'],
    ['vst', 'Variance stabilizing transformation (vst)'],
    ['mean_count', 'mean raw count'],
    ['min_count', 'minimum raw count'],
    ['max_count', 'maximum raw count'],
    ['min_norm_count', 'minimum normalized count'],
    ['mean_norm_count', 'mean normalized count'],
    ['max_norm_count', 'maximum normalized count'],
    ['percent_0_count', 'percent samples with 0 counts'],
    ['percent_low_count', 'percent samples with low counts (< 5)'],
    ['BH', 'Benjamini-Hochberg (BH)'],
    ['fdr', 'False Discovery Rate (FDR)'],
    ['BY', 'Benjamini-Yekutieli (BY)'],
    ['holm', 'Holm'],
    ['hochberg', 'Hochberg'],
    ['hommel', 'Hommel'],
    ['bonferroni', 'Bonferroni'],
    ['none', 'None'],
    ['padj', 'Adjusted P value'],
    ['pvalue', 'P value'],
    ['true', 'True'],
    ['false', 'False']
]);

/**
 * Given options like 'rld', 'vst', etc., returns a human-readable string.
 * If the key is not found in the mapping, it returns the key as-is.
 * Boolean values are converted to 'True' or 'False'.
 * Non-string keys are returned as-is.
 */
export const optionKeyToHuman = (key: any): any => {
    if (typeof key === 'string') {
        return optionKeyToHumanMapping.get(key) || key;
    } else if (typeof key === 'boolean') {
        return key ? 'True' : 'False';
    }
    return key;
};

interface ParameterNumber {
    type: string;
    type_human: string;
    description: string;
    min: number;
    max: number;
}

/**
 * Option presented to the user
 */
type Parameter = ParameterOptions | ParameterNumber;

/**
 * A category of parameters of analysis settings.
 * Each category contains a name and an array of parameters.
 */
interface Category {
    category: string;
    parameters: Parameter[];
}

/**
 * Categories are presented as collapsible Accordions. They can also be edited.
 */
interface CollapsibleCategory extends Category {
    /**
     * Whether the category (accordion) is open or closed.
     */
    open: boolean;
    /**
     * Whether the category is in edit mode.
     */
    editMode: boolean;
}

/**
 * Initializes the data for the settings form.
 *
 * Doing it with a lazy function ensures that state is set once and accidental
 * overrides dont propagate to other analyses.
 */
const initData = (): CollapsibleCategory[] => {
    const initialAnalyzeSettings: Category[] = [
        {
            category: 'Parameters for differential expression (DE) and normalization',
            parameters: [
                {
                    type: 'normalization_method',
                    type_human: 'Normalization method',
                    description: 'Select a method to normalize your data. `Vst` is recommended for larger datasets.',
                    options: ['rld', 'vst']
                },
                {
                    type: 'filter_source',
                    type_human: 'Metric to filter counts',
                    description:
                        'Select a metric to remove genes with low counts and determine the threshold for cutoff e.g., A metric of `mean count` and threshold of `5` will remove all genes that have a mean read count below five. A metric of `max count` and threshold of `10` will remove all genes that have a maximum read count below 10.',
                    options: [
                        'mean_count',
                        'min_count',
                        'max_count',
                        'min_norm_count',
                        'mean_norm_count',
                        'max_norm_count',
                        'percent_0_count',
                        'percent_low_count'
                    ]
                },
                {
                    type: 'filter_threshold',
                    type_human: 'Low count threshold',
                    description: 'Select a threshold for low counts.',
                    min: 0,
                    max: 99999
                },
                {
                    type: 'DE_padjust_method',
                    type_human: 'Method to adjust P values',
                    description: 'Select a method to adjust your P values. This helps account for false positives.',
                    options: ['BH', 'fdr', 'BY', 'holm', 'hochberg', 'hommel', 'bonferroni', 'none']
                },
                {
                    type: 'lfc_shrinkage',
                    type_human: 'Shrink Log2Fold changes',
                    description: 'Choose whether you want to shrink log2foldchanges.',
                    options: ['true', 'false']
                }
            ]
        },
        {
            category: 'Parameters for ontology, and initial filtering of significant genes',
            parameters: [
                {
                    type: 'pvalue_option',
                    type_human: 'P Value Source',
                    description: 'Select whether to determin significance from P values or adjusted P values.',
                    options: ['padj', 'pvalue']
                },
                {
                    type: 'pvalue_threshold',
                    type_human: 'P Value Cutoff',
                    description: 'Enter the cutoff for P value. Only genes with a P value below this threshold will be included.',
                    min: 0,
                    max: 1
                },
                {
                    type: 'fold_change_threshold',
                    type_human: 'Fold Change Cutoff',
                    description:
                        'Enter the cutoff for fold change. Only genes with an absolute fold change above this threshold will be included.',
                    min: 1,
                    max: 99999
                },
                {
                    type: 'if_separate_regulation',
                    type_human: 'Separate GO Analysis for Up vs Downregulated Genes',
                    description:
                        'Choose whether you want to separate differentially expressed genes by their direction in fold change into different gene lists (i.e., separate upregulated and downregulated genes).',
                    options: ['true', 'false']
                },
                {
                    type: 'ontology_pvalue_cutoff',
                    type_human: 'Ontology P Value Cutoff',
                    description:
                        'Enter the cutoff for P value for GO analysis. Only GO terms with a P value below this threshold will be included.',
                    min: 0,
                    max: 1
                },
                {
                    type: 'GO_padjust_method',
                    type_human: 'Ontology Method to Adjust P Values',
                    description: 'Select a method to adjust your P values for your GO terms',
                    options: ['BH', 'fdr', 'BY', 'holm', 'hochberg', 'hommel', 'bonferroni', 'none']
                }
            ]
        }
    ];
    return initialAnalyzeSettings.map(section => ({
        ...section,
        open: true,
        editMode: false
    }));
};

interface EditSettingsProps {
    formData: any;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const EditSettings: React.FC<EditSettingsProps> = ({formData, setFormData}) => {
    const [dataState, setDataState] = useState(initData);

    /**
     * Handles input changes in the settings form.
     * Updates the (parent) formData based on the input changes.
     *
     * @param name The name of the parameter being changed.
     * @param newValue The new value for the parameter.
     */
    const handleInputChange = (name: string, newValue: string) => {
        setFormData((prevState: any) => ({
            ...prevState,
            analyze_config: {
                ...prevState.analyze_config,
                [name]: newValue
            }
        }));
    };

    /**
     * Finds the section by index and updates the editMode to false.
     */
    const handleUpdateSettings = (index: number) => {
        setDataState(prevData =>
            prevData.map((section, i) => {
                if (i === index) {
                    return {...section, editMode: false};
                }
                return section;
            })
        );
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap="30px"
            marginBottom="24px"
            bgcolor={'surface-container-lowest'}
            p={3}
            borderRadius={'12px'}>
            <Box display={'flex'} gap={2} px={3} m={1} alignItems={'center'}>
                <HighlightOutlined sx={{fontSize: '42px', color: 'on-surface'}} />
                <div>
                    <Typography variant="title" size="medium">
                        Setting custom parameters
                    </Typography>
                    <Typography variant="body" size="medium">
                        We provide a set of default parameters for analyses. These can be modified to fit your needs. To learn more about
                        why or how you might changes these parameters, check out our Help Center resources.
                    </Typography>
                </div>
            </Box>
            {dataState.map((section, i) => (
                <Box borderRadius="10px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                    <Accordion
                        expanded={section.open}
                        sx={{
                            backgroundColor: 'unset',
                            boxShadow: 'unset',
                            '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                                minHeight: '0'
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {
                                borderBottom: '1px solid',
                                borderColor: 'outline-variant'
                            }
                        }}>
                        <AccordionSummary
                            aria-controls="panel1d-content"
                            id="panel1d-header"
                            onClick={() =>
                                setDataState(prevData =>
                                    prevData.map((item, idx) => {
                                        if (idx === i) {
                                            return {...item, open: !item.open};
                                        }
                                        return item;
                                    })
                                )
                            }
                            sx={{
                                backgroundColor: 'surface-container-highest',
                                padding: '4px 4px 4px 16px',
                                height: '0',
                                minHeight: '0',
                                gap: '12px',
                                flexDirection: 'row-reverse',
                                alignItems: 'center',
                                '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                    alignItems: 'center',
                                    margin: 0
                                },
                                '& .MuiAccordionSummary-root.Mui-expanded': {}
                            }}>
                            <Typography variant="title" size="medium" width="100%">
                                {section.category}
                            </Typography>
                            <Button
                                startIcon={section.editMode ? <Close /> : <EditOutlinedIcon />}
                                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                    e.stopPropagation();
                                    setDataState(prevData =>
                                        prevData.map((item, idx) => {
                                            if (idx === i) {
                                                return {...item, editMode: !item.editMode};
                                            }
                                            return item;
                                        })
                                    );
                                }}>
                                {section.editMode ? 'Cancel' : 'Edit'}
                            </Button>
                        </AccordionSummary>
                        {section.editMode ? (
                            <Box display="flex" flexDirection="column" gap="36px" padding="24px">
                                {section.parameters.map(item => {
                                    if (isParameterOptions(item)) {
                                        return (
                                            <TextField
                                                select
                                                label={item.type_human}
                                                helperText={item.description}
                                                value={formData.analyze_config[item.type]}
                                                onChange={e => handleInputChange(item.type, e.target.value)}>
                                                {item.options.map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {optionKeyToHuman(option)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        );
                                    } else {
                                        const inputProps = {
                                            min: item.min,
                                            max: item.max,
                                            step: item.max > 1 ? 0.5 : 0.05
                                        };
                                        return (
                                            <TextField
                                                type="number"
                                                label={item.type_human}
                                                helperText={item.description}
                                                value={formData.analyze_config[item.type]}
                                                onChange={e => handleInputChange(item.type, e.target.value)}
                                                inputProps={inputProps}
                                            />
                                        );
                                    }
                                })}
                                <Button
                                    sx={{borderRadius: '45px'}}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleUpdateSettings(i)}>
                                    Update Settings
                                </Button>
                            </Box>
                        ) : (
                            <Box display="flex" flexDirection="column" gap="15px" padding="20px">
                                {section.parameters.map(item => (
                                    <Box display="flex" gap="24px">
                                        <Typography variant="title" size="medium" width="400px">
                                            {item.type_human}
                                        </Typography>
                                        <Typography variant="body" size="large" width={310}>
                                            {optionKeyToHuman(formData.analyze_config[item.type])}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Accordion>
                </Box>
            ))}
        </Box>
    );
};

export default EditSettings;
